import useEmblaCarousel from 'embla-carousel-react';
import { Progress } from '~/common/components/ui/progress';
import type { HTMLAttributeAnchorTarget } from 'react';
import { useCallback, useEffect, useRef, useState, memo, useMemo } from 'react';
import type { EmblaCarouselType } from 'embla-carousel';
import { cn } from '~/common/utils/styleUtil';
import { getLinkRel } from '~/common/utils/urlUtil';
import { LucideArrowRight, LucidePause, LucidePlay } from 'lucide-react';
import { formatDate } from '~/common/utils/dateUtil';
import { Link, useLocation } from 'react-router';
import { useBasePath } from '~/common/hooks/useBasePath';
import type { IPromotionDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { useTranslation } from 'react-i18next';
import { REFERRER_URL_HINT, TARGET_URL } from '~/shell/routes/ShortcutResolverRoute.tsx';
import { SHORTCUT_RESOLVER_PATH } from '~/route-constants.ts';
import { usePromoImageUrl } from '~/common/utils/imageUrlProvider/useImageUrl';

export interface BannerProps {
  promotions: IPromotionDtoData[];
}

export function Banner({ promotions }: Readonly<BannerProps>) {
  const autoSlideInterval = 5000;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [progress, setProgress] = useState(0);
  const [innerPromotions, setInnerPromotions] = useState<IPromotionDtoData[]>(promotions);

  const timeoutRef = useRef<number | null>(null);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const pausedTimeRef = useRef<number | null>(null);

  const isAutoplayPausedRef = useRef(false);
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);

  const startAutoplay = useCallback(() => {
    if (!emblaApi) return;

    startTimeRef.current = performance.now();
    timeoutRef.current = window.setTimeout(() => {
      emblaApi.scrollNext();
    }, autoSlideInterval);

    const updateProgress = () => {
      const now = performance.now();
      const elapsed = now - (startTimeRef.current ?? now);
      const percent = Math.min((elapsed / autoSlideInterval) * 100, 100);

      setProgress(prev => {
        const newPercent = Math.round(percent);
        return prev !== newPercent ? newPercent : prev;
      });

      if (percent < 100 && !isAutoplayPausedRef.current) {
        setTimeout(() => {
          animationRef.current = requestAnimationFrame(updateProgress);
        }, 100);
      }
    };

    animationRef.current = requestAnimationFrame(updateProgress);
  }, [emblaApi]);

  const pauseAutoplay = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    pausedTimeRef.current = performance.now();
    isAutoplayPausedRef.current = true;
    setIsAutoplayPaused(true);
  }, []);

  const resumeAutoplay = useCallback(() => {
    if (!emblaApi || !startTimeRef.current || !pausedTimeRef.current) {
      startAutoplay();
      isAutoplayPausedRef.current = false;
      setIsAutoplayPaused(false);
      return;
    }

    const elapsedBeforePause = pausedTimeRef.current - startTimeRef.current;
    const remaining = autoSlideInterval - elapsedBeforePause;
    startTimeRef.current = performance.now() - elapsedBeforePause;

    timeoutRef.current = window.setTimeout(() => {
      emblaApi.scrollNext();
    }, remaining);

    const updateProgress = () => {
      const now = performance.now();
      const elapsed = now - startTimeRef.current!;
      const percent = Math.min((elapsed / autoSlideInterval) * 100, 100);
      setProgress(percent);

      if (percent < 100 && !isAutoplayPausedRef.current) {
        animationRef.current = requestAnimationFrame(updateProgress);
      }
    };

    animationRef.current = requestAnimationFrame(updateProgress);
    isAutoplayPausedRef.current = false;
    setIsAutoplayPaused(false);
  }, [emblaApi, startAutoplay]);

  const toggleAutoplay = useCallback(() => {
    if (isAutoplayPausedRef.current) {
      resumeAutoplay();
    } else {
      pauseAutoplay();
    }
  }, [pauseAutoplay, resumeAutoplay]);

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onErrorLoadingPromotionImage = useCallback((celumId: number) => {
    setInnerPromotions(prev => prev.filter(p => p.celumId !== celumId));
  }, []);

  const onSelect = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }

      setSelectedIndex(emblaApi.selectedScrollSnap());
      setProgress(0);

      if (!isAutoplayPausedRef.current) {
        startAutoplay();
      }
    },
    [startAutoplay]
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing with external Embla carousel API on init
    onInit(emblaApi);
    onSelect(emblaApi);

    emblaApi.on('select', onSelect).on('reInit', () => {
      onInit(emblaApi);
      onSelect(emblaApi);
    });

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [emblaApi, onInit, onSelect]);

  const banners = useMemo(() => {
    return (
      <div className="overflow-hidden" ref={emblaRef} onMouseEnter={pauseAutoplay} onMouseLeave={resumeAutoplay}>
        <div className="flex bg-neutral-100 desktop:rounded">
          {innerPromotions.map(promotion => (
            <BannerPromotion
              key={promotion.id}
              promotion={promotion}
              onErrorLoadingPromotionImage={onErrorLoadingPromotionImage}
            />
          ))}
        </div>
      </div>
    );
  }, [emblaRef, innerPromotions, onErrorLoadingPromotionImage, pauseAutoplay, resumeAutoplay]);

  if (innerPromotions?.length === 0) return null;

  return (
    <div className={'overflow-hidden w-full'} data-testid="banner-container">
      {banners}
      {scrollSnaps.length > 1 ? (
        <div className="flex items-center px-2 desktop:px-0">
          <button type="button"
            className="focus:outline-none mr-2 text-secondary hover:text-primary"
            onClick={toggleAutoplay}
            data-testid="banner-play-button"
          >
            {isAutoplayPaused ? <LucidePlay size={16} /> : <LucidePause size={16} />}
          </button>
          <div className={cn('flex-1 flex h-fit gap-1')}>
            {scrollSnaps.map((snapPosition, index) => (
              <button type="button"
                key={snapPosition}
                className="flex-1 py-2"
                onClick={() => onDotButtonClick(index)}
                data-testid={`banner-index-button-${index}`}
              >
                <Progress className="h-1 bg-neutral-300" value={selectedIndex === index ? progress + 1 : 0} />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="h-5" />
      )}
    </div>
  );
}

interface BannerPromotionProps {
  promotion: IPromotionDtoData;
  onErrorLoadingPromotionImage: (celumId: number) => void;
}

const BannerPromotion = memo(function BannerPromotion({
  promotion,
  onErrorLoadingPromotionImage
}: BannerPromotionProps) {
  const { path } = useBasePath();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const imgSrc = usePromoImageUrl(promotion.celumId);

  let promotionLinkUrl: string;
  let promotionLinkTarget: HTMLAttributeAnchorTarget;
  if (promotion.promotionLinkUrl) {
    // Promotion wurde in ProlixPro mit einem spezifischen Link konfiguriert

    if (promotion.useExternalLink) {
      // Es ist ein externer Link, dieser soll in einem neuen Tab geöffnet werden.
      promotionLinkUrl = promotion.promotionLinkUrl;
      promotionLinkTarget = '_blank';
    } else {
      // Es ist ein interner Link, wir routen den Request auf die ShortCutResolverRoute, damit Shortcut-URLs korrekt behandelt werden
      const encodedTargetUrl = encodeURIComponent(promotion.promotionLinkUrl);
      promotionLinkUrl = `/${SHORTCUT_RESOLVER_PATH}?${TARGET_URL}=${encodedTargetUrl}&${REFERRER_URL_HINT}=${pathname}`;
      promotionLinkTarget = '_self';
    }
  } else {
    // Promotion wurde in ProlixPro nicht mit einem spezifischen Link konfiguriert.
    // Die Promotion sollte aber verknüpfte Artikel konfiguriert haben und die Navigation auf die interne Promotion Route zeigt dann diese Artikle an
    promotionLinkUrl = `${path}/promotion/${promotion.id}`; // Navigation to the "internal" Promotion Page
    promotionLinkTarget = '_self';
  }

  return (
    <Link
      key={promotion.name + promotion.celumId}
      to={promotionLinkUrl}
      target={promotionLinkTarget}
      className="@container flex-[0_0_100%] [transform:translate3d(0,0,0)] h-fit"
      rel={getLinkRel(promotionLinkUrl)}
    >
      <div className="flex flex-col @2xl:flex-row flex-nowrap rounded bg-neutral-100">
        <div>
          <img
            className="rounded-t @2xl:rounded-l @2xl:rounded-r-none w-full h-auto object-cover"
            data-testid={`banner-img-${promotion.id}`}
            alt={promotion.name}
            src={imgSrc}
            onError={() => onErrorLoadingPromotionImage(promotion.celumId)}
          />
        </div>
        <div className="flex items-center p-4 @4xl:p-8 text-primary rounded-b @2xl:rounded-l-none @2xl:rounded-r min-w-[300px] @4xl:min-w-[400px] min-h-28">
          <div>
            {promotion.dateVisible && (
              <p className="text-sm">
                {formatDate(new Date(promotion.startDate))} - {formatDate(new Date(promotion.endDate))}
              </p>
            )}
            <h2 className="font-normal text-xl @4xl:text-2xl">{promotion.name}</h2>
            <div
              className="@2xl:mt-4 underline underline-offset-4 flex items-center"
              data-testid={`banner-more-link-${promotion.id}`}
            >
              {t('Mehr ansehen')}
              <LucideArrowRight size={16} className="ml-1" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
});
